import { useMutation, useQuery } from "@apollo/client";
import Accordion from "@dashboard/components/Accordion";
import { Button } from "@dashboard/components/Button";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import makeCreatorSteps, { Step } from "@dashboard/components/CreatorSteps";
import { DashboardModal } from "@dashboard/components/Modal";
import { useBaseChannelsQuery } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { useTemplateDownload } from "@dashboard/imports/hooks/useTemplateDownload";
import { importProductsMutation } from "@dashboard/imports/mutations";
import { importFileStatusQuery } from "@dashboard/imports/queries";
import {
  DuplicateStrategy,
  ImportError,
  ImportFile,
  ImportFileStatus,
  ImportSheetSummary,
  ImportType,
} from "@dashboard/imports/types";
import { IMPORT_TEMPLATE_URL } from "@dashboard/imports/urls";
import { buttonMessages } from "@dashboard/intl";
import { Box, Divider, Text } from "@saleor/macaw-ui-next";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { messages } from "./messages";

interface ImportModalProps {
  open: boolean;
  importType: ImportType;
  onClose: () => void;
}

// ── Wizard steps ─────────────────────────────────────────────────────────────

enum ImportStep {
  CONFIGURE = 0,
  UPLOAD = 1,
}

function useSteps(): Array<Step<ImportStep>> {
  const intl = useIntl();

  return [
    {
      label: intl.formatMessage(messages.stepConfigure),
      value: ImportStep.CONFIGURE,
    },
    {
      label: intl.formatMessage(messages.stepUpload),
      value: ImportStep.UPLOAD,
    },
  ];
}

const ImportSteps = makeCreatorSteps<ImportStep>();
const POLL_INTERVAL_MS = 3000;

const hasImportIssues = (summary: ImportSheetSummary[] = []) =>
  summary.some(sheet => sheet.sheetMissing || sheet.rowsSkipped > 0 || sheet.rowsError > 0) ||
  (summary.length > 0 && getProcessedRowCount(summary) === 0);

const getProcessedRowCount = (summary: ImportSheetSummary[] = []) =>
  summary.reduce((total, sheet) => total + sheet.rowsOk + sheet.rowsSkipped + sheet.rowsError, 0);

const getImportSummaryText = (summary: ImportSheetSummary[] = []) =>
  summary
    .map(sheet =>
      sheet.sheetMissing
        ? `${sheet.sheet.replace(/_/g, " ")}: sheet missing`
        : `${sheet.sheet.replace(/_/g, " ")}: ${sheet.rowsOk} imported, ${sheet.rowsSkipped} skipped, ${sheet.rowsError} errors`,
    )
    .join("; ");

const LANGUAGE_OPTIONS = [
  { code: "pt", label: "Português (PT)" },
  { code: "pt-BR", label: "Português (BR)" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "zh-Hans", label: "中文 (简体)" },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const ImportModal = ({ open, importType, onClose }: ImportModalProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { download: downloadTemplate } = useTemplateDownload();

  const [step, setStep] = useState<ImportStep>(ImportStep.CONFIGURE);
  const [file, setFile] = useState<File | null>(null);
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>(
    DuplicateStrategy.UPDATE,
  );
  const [contentLanguage, setContentLanguage] = useState<string>("pt");
  const [channelSlug, setChannelSlug] = useState("");
  const [importFileId, setImportFileId] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<ImportFile | null>(null);
  const [submitErrors, setSubmitErrors] = useState<ImportError[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmButtonTransitionState>("default");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps = useSteps();
  const templateUrl = IMPORT_TEMPLATE_URL(importType.toLowerCase());
  const { data: channelData } = useBaseChannelsQuery({ displayLoader: true });

  const { refetch: fetchImportFile } = useQuery(importFileStatusQuery, {
    skip: true,
    fetchPolicy: "network-only",
  });

  const [submitImport] = useMutation(importProductsMutation, {
    onCompleted: data => {
      const result = data?.importProducts;

      if (result?.importErrors?.length > 0) {
        setSubmitErrors(result.importErrors);
        setConfirmState("error");
        notify({
          status: "error",
          title: intl.formatMessage(messages.toastErrorTitle),
          text: result.importErrors.map((e: ImportError) => e.message).join(", "),
        });

        return;
      }

      const id = result?.importFile?.id;

      if (id) {
        setImportFileId(id);
        setConfirmState("loading");
        notify({
          title: intl.formatMessage(messages.toastStartedTitle),
          text: intl.formatMessage(messages.toastStartedText),
        });
      }
    },
  });

  // Polling
  useEffect(() => {
    if (!importFileId) return;

    pollRef.current = setInterval(async () => {
      const result = await fetchImportFile({ id: importFileId });
      const fileData = result?.data?.importFile;

      if (!fileData) return;

      setImportFile(fileData);

      if (fileData.status === ImportFileStatus.SUCCESS) {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setConfirmState("success");

        const summary = fileData.resultSummary ?? [];
        const summaryText = getImportSummaryText(summary);
        const hasIssues = hasImportIssues(summary);
        const noRowsProcessed = getProcessedRowCount(summary) === 0;

        notify({
          status: hasIssues || noRowsProcessed ? "warning" : "success",
          title: intl.formatMessage(
            hasIssues || noRowsProcessed ? messages.toastIssuesTitle : messages.toastSuccessTitle,
          ),
          text: noRowsProcessed
            ? intl.formatMessage(messages.noRowsProcessed)
            : summaryText || intl.formatMessage(messages.toastSuccessText),
        });
      } else if (fileData.status === ImportFileStatus.FAILED) {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setConfirmState("error");
        notify({
          status: "error",
          title: intl.formatMessage(messages.toastFailedTitle),
          text: intl.formatMessage(messages.toastFailedText),
        });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [importFileId, fetchImportFile]);

  const resetState = () => {
    setStep(ImportStep.CONFIGURE);
    setFile(null);
    setDuplicateStrategy(DuplicateStrategy.UPDATE);
    setContentLanguage("pt");
    setChannelSlug("");
    setImportFileId(null);
    setImportFile(null);
    setSubmitErrors([]);
    setConfirmState("default");

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    if (!file) return;

    setSubmitErrors([]);
    setConfirmState("loading");
    submitImport({
      variables: {
        input: { file, importType, duplicateStrategy, channelSlug: channelSlug || null },
      },
    });
  };

  const isDone =
    importFile?.status === ImportFileStatus.SUCCESS ||
    importFile?.status === ImportFileStatus.FAILED;

  const isProcessing = !!importFileId && !isDone;

  return (
    <DashboardModal onChange={handleClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          <FormattedMessage {...messages.title} />
        </DashboardModal.Header>

        <ImportSteps currentStep={step} steps={steps} onStepClick={setStep} />

        {/* ── Step 0: Configure ───────────────────────────────────────────── */}
        {step === ImportStep.CONFIGURE && (
          <Box display="flex" flexDirection="column" gap={4}>
            {/* Template download */}
            <Accordion title={intl.formatMessage(messages.sectionTemplate)}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.templateDescription} />
                </Text>
                <Button
                  variant="secondary"
                  onClick={() =>
                    downloadTemplate(templateUrl, `template-${importType.toLowerCase()}.xlsx`)
                  }
                >
                  <FormattedMessage {...messages.downloadTemplateButton} />
                </Button>
              </Box>
            </Accordion>

            {/* Content language */}
            <Accordion title={intl.formatMessage(messages.sectionLanguage)}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.languageDescription} />
                </Text>
                <select
                  value={contentLanguage}
                  onChange={e => setContentLanguage(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {LANGUAGE_OPTIONS.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </Box>
            </Accordion>

            {/* Optional default channel for variant listings */}
            <Accordion title={intl.formatMessage(messages.sectionChannel)}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.channelDescription} />
                </Text>
                <select
                  value={channelSlug}
                  onChange={e => setChannelSlug(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  <option value="">{intl.formatMessage(messages.noChannelSelected)}</option>
                  {(channelData?.channels ?? [])
                    .filter(channel => channel.isActive)
                    .map(channel => (
                      <option key={channel.id} value={channel.slug}>
                        {channel.name}
                      </option>
                    ))}
                </select>
              </Box>
            </Accordion>

            {/* Duplicate strategy */}
            <Accordion title={intl.formatMessage(messages.sectionDuplicate)}>
              <Box display="flex" flexDirection="column" gap={2}>
                {(
                  [
                    {
                      value: DuplicateStrategy.UPDATE,
                      label: messages.strategyUpdate,
                      description: messages.strategyUpdateDesc,
                    },
                    {
                      value: DuplicateStrategy.SKIP,
                      label: messages.strategySkip,
                      description: messages.strategySkipDesc,
                    },
                    {
                      value: DuplicateStrategy.ERROR,
                      label: messages.strategyError,
                      description: messages.strategyErrorDesc,
                    },
                  ] as const
                ).map(({ value, label, description }) => (
                  <Box
                    key={value}
                    display="flex"
                    alignItems="flex-start"
                    gap={3}
                    paddingY={2}
                    style={{ cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}
                    onClick={() => setDuplicateStrategy(value)}
                  >
                    <input
                      type="radio"
                      id={`strategy-${value}`}
                      name="duplicateStrategy"
                      value={value}
                      checked={duplicateStrategy === value}
                      onChange={() => setDuplicateStrategy(value)}
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <Box>
                      <Text size={3} fontWeight="medium" display="block">
                        <FormattedMessage {...label} />
                      </Text>
                      <Text size={2} color="default2" display="block">
                        <FormattedMessage {...description} />
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Accordion>
          </Box>
        )}

        {/* ── Step 1: Upload & Import ─────────────────────────────────────── */}
        {step === ImportStep.UPLOAD && (
          <Box display="flex" flexDirection="column" gap={4}>
            {/* File picker */}
            <Accordion title={intl.formatMessage(messages.sectionUpload)} initialExpand>
              <Box display="flex" flexDirection="column" gap={3}>
                <Text size={2} color="default2">
                  <FormattedMessage {...messages.uploadDescription} />
                </Text>
                <Box display="flex" alignItems="center" gap={3}>
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                  >
                    <FormattedMessage {...messages.chooseFile} />
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    onChange={e => setFile(e.target.files?.[0] ?? null)}
                  />
                  <Text size={2} color={file ? "default1" : "default2"}>
                    {file ? file.name : <FormattedMessage {...messages.noFileSelected} />}
                  </Text>
                </Box>
              </Box>
            </Accordion>

            {/* Summary of settings */}
            <Accordion title={intl.formatMessage(messages.sectionSummary)}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between">
                  <Text size={2} color="default2">
                    <FormattedMessage {...messages.summaryType} />
                  </Text>
                  <Text size={2}>{importType}</Text>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Text size={2} color="default2">
                    <FormattedMessage {...messages.summaryDuplicate} />
                  </Text>
                  <Text size={2}>{duplicateStrategy}</Text>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Text size={2} color="default2">
                    <FormattedMessage {...messages.summaryLanguage} />
                  </Text>
                  <Text size={2}>
                    {LANGUAGE_OPTIONS.find(l => l.code === contentLanguage)?.label ??
                      contentLanguage}
                  </Text>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Text size={2} color="default2">
                    <FormattedMessage {...messages.summaryChannel} />
                  </Text>
                  <Text size={2}>
                    {channelData?.channels?.find(channel => channel.slug === channelSlug)?.name ??
                      intl.formatMessage(messages.noChannelSelected)}
                  </Text>
                </Box>
              </Box>
            </Accordion>

            {/* Status / result */}
            {isDone && (
              <Box
                padding={4}
                borderRadius={3}
                backgroundColor={
                  importFile?.status !== ImportFileStatus.SUCCESS
                    ? "critical1"
                    : hasImportIssues(importFile?.resultSummary)
                      ? "warning1"
                      : "success1"
                }
              >
                <Text size={2} color="buttonDefaultPrimary" fontWeight="medium" display="block">
                  {importFile?.status !== ImportFileStatus.SUCCESS ? (
                    <FormattedMessage {...messages.importFailed} />
                  ) : hasImportIssues(importFile?.resultSummary) ? (
                    <FormattedMessage {...messages.toastIssuesTitle} />
                  ) : (
                    <FormattedMessage {...messages.importSuccess} />
                  )}
                </Text>
                {(importFile?.resultSummary.length ?? 0) > 0 && (
                  <Box marginTop={2}>
                    <Text size={2} display="block">
                      {getImportSummaryText(importFile?.resultSummary)}
                    </Text>
                    <Box marginTop={2}>
                      <Text size={2} fontWeight="medium" display="block">
                        <FormattedMessage {...messages.resultSummary} />
                      </Text>
                      {importFile?.resultSummary.map(sheet => (
                        <Box key={sheet.sheet}>
                          <Text size={2} display="block">
                            {sheet.sheet.replace(/_/g, " ")}:{" "}
                            {sheet.sheetMissing ? (
                              <FormattedMessage {...messages.missingSheet} />
                            ) : (
                              <FormattedMessage
                                {...messages.resultSummaryLine}
                                values={{
                                  ok: sheet.rowsOk,
                                  skipped: sheet.rowsSkipped,
                                  errors: sheet.rowsError,
                                }}
                              />
                            )}
                          </Text>
                          {sheet.errorMessages.map((error, index) => (
                            <Text
                              key={`${sheet.sheet}-error-${index}`}
                              size={1}
                              color="critical1"
                              display="block"
                            >
                              • {error}
                            </Text>
                          ))}
                          {sheet.skipMessages.map((message, index) => (
                            <Text
                              key={`${sheet.sheet}-skip-${index}`}
                              size={1}
                              color="warning1"
                              display="block"
                            >
                              • {message}
                            </Text>
                          ))}
                          {sheet.rowsError > sheet.errorMessages.length && (
                            <Text size={1} color="default2" display="block">
                              <FormattedMessage
                                {...messages.moreErrors}
                                values={{ count: sheet.rowsError - sheet.errorMessages.length }}
                              />
                            </Text>
                          )}
                          {sheet.rowsSkipped > sheet.skipMessages.length && (
                            <Text size={1} color="default2" display="block">
                              <FormattedMessage
                                {...messages.moreSkips}
                                values={{ count: sheet.rowsSkipped - sheet.skipMessages.length }}
                              />
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {importFile?.resultUrl && (
                  <Box marginTop={2}>
                    <a href={importFile.resultUrl} target="_blank" rel="noreferrer">
                      <Button variant="secondary" size="small">
                        <FormattedMessage {...messages.downloadResult} />
                      </Button>
                    </a>
                  </Box>
                )}
              </Box>
            )}

            {/* Mutation errors */}
            {submitErrors.length > 0 && (
              <Box>
                {submitErrors.map((err, i) => (
                  <Text key={i} display="block" color="critical1" size={2}>
                    {err.message}
                  </Text>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Footer actions ─────────────────────────────────────────────── */}
        <DashboardModal.Actions>
          {step === ImportStep.CONFIGURE && (
            <>
              <Button variant="secondary" color="text" onClick={handleClose}>
                <FormattedMessage {...buttonMessages.cancel} />
              </Button>
              <Button variant="primary" onClick={() => setStep(ImportStep.UPLOAD)}>
                <FormattedMessage {...buttonMessages.nextStep} />
              </Button>
            </>
          )}

          {step === ImportStep.UPLOAD && (
            <>
              {!importFileId && (
                <Button
                  variant="secondary"
                  color="text"
                  onClick={() => setStep(ImportStep.CONFIGURE)}
                >
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
              )}
              {isDone ? (
                <Button variant="primary" onClick={handleClose}>
                  <FormattedMessage {...buttonMessages.ok} />
                </Button>
              ) : (
                <ConfirmButton
                  transitionState={confirmState}
                  disabled={!file || isProcessing}
                  onClick={handleSubmit}
                >
                  <FormattedMessage {...messages.submitImport} />
                </ConfirmButton>
              )}
            </>
          )}
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default ImportModal;
